import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Header from "../components/Header";
import Footer from "../components/Footer";
import FloatingContact from "../components/FloatingContact";
import ApplicationDialog from "../components/ApplicationDialog";
import { useLang } from "../i18n/LangContext";
import blog, { LOCALE_TO_URL_SEG, LOCALE_TO_HTML_LANG } from "../i18n/blog_i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = "https://studygri.com";
const LIMIT = 12;

const BlogListPage = () => {
  const { lang } = useLang();
  const t = blog[lang] || blog.uk;
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/articles/public`, { params: { locale: lang, page, limit: LIMIT } })
      .then(r => { if (!cancelled) { setItems(r.data.items || []); setTotal(r.data.total || 0); } })
      .catch(() => { if (!cancelled) { setItems([]); setTotal(0); } })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [lang, page]);

  useEffect(() => {
    document.documentElement.lang = LOCALE_TO_HTML_LANG[lang] || lang;
    document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    return () => { document.documentElement.dir = 'ltr'; };
  }, [lang]);

  const seg = LOCALE_TO_URL_SEG[lang] || 'ua';
  const basePath = '/blog';  // React Router prepends /{seg} via basename
  const canonical = `${SITE_URL}/${seg}/blog${page > 1 ? `?page=${page}` : ''}`;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const fmtDate = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleDateString(lang === 'uk' ? 'uk-UA' : lang, { day: '2-digit', month: 'long', year: 'numeric' }); }
    catch { return iso.slice(0, 10); }
  };

  return (
    <div className="min-h-screen" style={{ background: '#FAF8F5' }}>
      <Helmet>
        <title>{`${t.title} — STUDYGRI`}</title>
        <meta name="description" content={t.subtitle} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${t.title} — STUDYGRI`} />
        <meta property="og:description" content={t.subtitle} />
        <meta property="og:url" content={canonical} />
      </Helmet>

      <Header onOpenForm={() => setFormOpen(true)} />

      <main className="pt-28 md:pt-36 pb-20">
        <div className="max-w-6xl mx-auto px-5 md:px-8">
          <div className="mb-10 md:mb-14">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-tight" style={{ color: '#1B2A4A' }} data-testid="blog-title">
              {t.title}
            </h1>
            <p className="mt-3 text-base md:text-lg" style={{ color: '#666' }}>{t.subtitle}</p>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[0,1,2].map(i => (
                <div key={i} className="rounded-2xl bg-white h-80 animate-pulse" style={{ border: '1px solid #E8E0D5' }} />
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="text-center py-20 text-lg" style={{ color: '#666' }} data-testid="blog-empty">{t.empty}</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {items.map(a => (
                <Link
                  key={a.id}
                  to={`${basePath}/${a.slug}`}
                  className="group rounded-2xl bg-white overflow-hidden transition hover:shadow-xl hover:-translate-y-1"
                  style={{ border: '1px solid #E8E0D5', boxShadow: '0 8px 24px -18px rgba(27,42,74,0.12)' }}
                  data-testid={`blog-card-${a.slug}`}
                >
                  <div className="aspect-[16/10] overflow-hidden" style={{ background: '#EDE6D8' }}>
                    {a.cover_url ? (
                      <img src={a.cover_url} alt={a.title} loading="lazy" className="w-full h-full object-cover transition group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center font-script text-4xl" style={{ color: '#C9A96E' }}>studygri</div>
                    )}
                  </div>
                  <div className="p-6">
                    <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: '#C9A96E' }}>
                      {fmtDate(a.published_at)}
                    </div>
                    <h2 className="font-display text-xl md:text-2xl leading-snug mb-3 transition group-hover:text-[#C9A96E]" style={{ color: '#1B2A4A' }}>
                      {a.title}
                    </h2>
                    {a.excerpt && (
                      <p className="text-sm leading-relaxed line-clamp-3" style={{ color: '#5a5a5a' }}>{a.excerpt}</p>
                    )}
                    <div className="mt-5 text-sm font-semibold" style={{ color: '#1B2A4A' }}>{t.readMore}</div>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {totalPages > 1 && (
            <div className="mt-14 flex items-center justify-center gap-6">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:text-[#C9A96E]"
                style={{ color: '#1B2A4A' }}
                data-testid="blog-prev-page"
              >{t.prevPage}</button>
              <span className="text-sm" style={{ color: '#666' }}>{t.page} {page} / {totalPages}</span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="text-sm font-semibold disabled:opacity-30 disabled:cursor-not-allowed hover:text-[#C9A96E]"
                style={{ color: '#1B2A4A' }}
                data-testid="blog-next-page"
              >{t.nextPage}</button>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <FloatingContact onOpenForm={() => setFormOpen(true)} />
      <ApplicationDialog open={formOpen} onOpenChange={setFormOpen} />
    </div>
  );
};

export default BlogListPage;
